import BigNumber from 'bignumber.js';
import { distinctBigNumberUntilChanged, sharePublishReplay } from 'ngx-libs';
import { BehaviorSubject, combineLatest, Observable, timer } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { EthWalletService } from '../services/eth-wallet.service';
import { Expiry } from './expiry';
import { Market } from './market';

const impliedVolatility = require('implied-volatility');
const greeks = require('greeks');

interface Greeks {
  delta: BigNumber;
  gamma: BigNumber;
  vega: BigNumber;
}

export class Strike {
  buyCallPrice: Observable<BigNumber>;
  sellCallPrice: Observable<BigNumber>;
  buyPutPrice: Observable<BigNumber>;
  sellPutPrice: Observable<BigNumber>;

  buyCallIv: Observable<number>;
  sellCallIv: Observable<number>;
  buyPutIv: Observable<number>;
  sellPutIv: Observable<number>;

  callGreeks: Observable<Greeks>;
  putGreeks: Observable<Greeks>;

  buyCallDisabled: Observable<boolean>;
  sellCallDisabled: Observable<boolean>;
  buyPutDisabled: Observable<boolean>;
  sellPutDisabled: Observable<boolean>;

  callItm: Observable<boolean>;
  putItm: Observable<boolean>;

  private dataSubject = new BehaviorSubject({});

  constructor(
    public price: BigNumber,
    private ethWalletService: EthWalletService,
    // private mainService: MainService,
    private market: Market,
    public expiry: Expiry
  ) {
    this.buyCallDisabled = this.dataSubject.pipe(map((data: any) => data.buyCallDisabled));
    this.sellCallDisabled = this.dataSubject.pipe(map((data: any) => data.sellCallDisabled));
    this.buyPutDisabled = this.dataSubject.pipe(map((data: any) => data.buyPutDisabled));
    this.sellPutDisabled = this.dataSubject.pipe(map((data: any) => data.sellPutDisabled));

    const poolChange = this.market.pools.pipe(
      switchMap((pools) => combineLatest(pools.map((p) => p.marginBalance))),
      debounceTime(200)
    );

    const spotPrice = this.market.spotPricer.price;
    const source = combineLatest([this.dataSubject, poolChange, spotPrice, this.market.adjustUpdater, timer(0, 5000)]);

    const createPrice = (side: string, type: string) => {
      return source.pipe(
        debounceTime(10),
        switchMap(() => this.market.vault.getStrikeExtra(this.expiry.timestamp, this.price, side, type, new BigNumber(1))),
        map((r) => r.price),
        distinctBigNumberUntilChanged(),
        sharePublishReplay(1)
      );
    };

    this.buyCallPrice = createPrice('buy', 'call');
    this.sellCallPrice = createPrice('sell', 'call');
    this.buyPutPrice = createPrice('buy', 'put');
    this.sellPutPrice = createPrice('sell', 'put');

    const createIv = (price: Observable<BigNumber>, type: string) => {
      return combineLatest([price, spotPrice]).pipe(
        map(([price, spot]) => {
          const time = this.getTimeToExperiation();
          const p = price.toNumber();
          return p === 0
            ? 0
            : impliedVolatility.getImpliedVolatility(
                p,
                spot.toNumber(),
                this.price.toNumber(),
                time,
                0.06,
                type.toLowerCase()
              );
        }),
        distinctUntilChanged(),
        sharePublishReplay(1)
      );
    };

    this.buyCallIv = createIv(this.buyCallPrice, 'call');
    this.sellCallIv = createIv(this.sellCallPrice, 'call');
    this.buyPutIv = createIv(this.buyPutPrice, 'put');
    this.sellPutIv = createIv(this.sellPutPrice, 'put');

    const createGreeks = (iv: Observable<number>, type: string) => {
      return combineLatest([iv, spotPrice]).pipe(
        map(([i, spot]) => {
          const time = this.getTimeToExperiation();
          return {
            delta: new BigNumber(greeks.getDelta(spot.toNumber(), this.price.toNumber(), time, i, 0.06, type)),
            gamma: new BigNumber(greeks.getGamma(spot.toNumber(), this.price.toNumber(), time, i, 0.06, type)),
            vega: new BigNumber(greeks.getVega(spot.toNumber(), this.price.toNumber(), time, i, 0.06, type)),
          };
        }),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        sharePublishReplay(1)
      );
    };


    const buyCallIv = this.dataSubject.pipe(map((data: any) => data?.buyCallIv?.toNumber() || 0));
    const sellCallIv = this.dataSubject.pipe(map((data: any) => data?.sellCallIv?.toNumber() || 0));
    const callIv = combineLatest([buyCallIv, sellCallIv]).pipe(
      map(([b, s]) => (b + s) / 2)
    );
    const buyPutIv = this.dataSubject.pipe(map((data: any) => data?.buyPutIv?.toNumber() || 0));
    const sellPutIv = this.dataSubject.pipe(map((data: any) => data?.sellPutIv?.toNumber() || 0));
    const putIv = combineLatest([buyCallIv, sellCallIv]).pipe(
      map(([b, s]) => (b + s) / 2)
    );

    this.callGreeks = createGreeks(callIv, 'call');
    this.putGreeks = createGreeks(putIv, 'put');

    this.callItm = spotPrice.pipe(
      map((s) => s.gte(this.price))
    );

    this.putItm = spotPrice.pipe(
      map((s) => s.lte(this.price))
    );
  }

  updateData(data: any) {
    this.market.contractVault.optionMarket.setIv(this.expiry.timestamp, this.price.toDecimal(), true, true, data.buyCallIv.toDecimal());
    this.market.contractVault.optionMarket.setIv(this.expiry.timestamp, this.price.toDecimal(), true, false, data.sellCallIv.toDecimal());
    this.market.contractVault.optionMarket.setIv(this.expiry.timestamp, this.price.toDecimal(), false, true, data.buyPutIv.toDecimal());
    this.market.contractVault.optionMarket.setIv(this.expiry.timestamp, this.price.toDecimal(), false, false, data.sellPutIv.toDecimal());
    this.dataSubject.next(data);
  }

  private getTimeToExperiation() {
    return (this.expiry.timestamp - Date.now() / 1000) / 31536000;
  }
}
